package org.moxultimate.topwithmox.tech.ui

import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.launch
import org.moxultimate.topwithmox.core.base.ViewModel
import org.moxultimate.topwithmox.core.state.APIState
import org.moxultimate.topwithmox.tech.api.TechRepository
import org.moxultimate.topwithmox.tech.data.TechResponse

/**
 * TechViewModel
 * Manages cursor-based pagination and client-side type/search filtering for tech entries.
 * Filters apply over the already-loaded [accumulatedTechs] without triggering a new API call.
 * Type filter uses OR logic across selected types.
 * Search filter matches [TechResponse.title] and [TechResponse.tags].
 *
 * @param repository Data source for tech entries, tags, and types.
 */
class TechViewModel(
    private val repository: TechRepository,
) : ViewModel() {

    private val _screenState = MutableStateFlow<TechState>(TechState.Loading)
    val screenState: StateFlow<TechState> = _screenState

    private val _isLoadingMore = MutableStateFlow(false)
    val isLoadingMore: StateFlow<Boolean> = _isLoadingMore

    private val _hasMore = MutableStateFlow(true)
    val hasMore: StateFlow<Boolean> = _hasMore

    private val _allTypes = MutableStateFlow<List<String>>(emptyList())
    val allTypes: StateFlow<List<String>> = _allTypes

    private val _allTags = MutableStateFlow<List<String>>(emptyList())
    val allTags: StateFlow<List<String>> = _allTags

    private val _selectedTypes = MutableStateFlow<Set<String>>(emptySet())
    val selectedTypes: StateFlow<Set<String>> = _selectedTypes

    private val _searchText = MutableStateFlow("")
    val searchText: StateFlow<String> = _searchText

    private val accumulatedTechs = mutableListOf<TechResponse>()
    private var lastId: Long = Long.MAX_VALUE
    private var hasMoreItems = true

    init {
        loadTechs()
        loadAllTypes()
        loadAllTags()
    }

    /**
     * loadTechs
     * Resets accumulated state and fetches the first page of tech entries.
     */
    fun loadTechs() {
        accumulatedTechs.clear()
        lastId = Long.MAX_VALUE
        hasMoreItems = true
        _hasMore.value = true
        fetchPage(isInitialLoad = true)
    }

    /**
     * loadMoreIfNeeded
     * Called by the scroll handler when the user approaches the bottom.
     * No-op when already loading or no more pages exist.
     */
    fun loadMoreIfNeeded() {
        if (hasMoreItems && !_isLoadingMore.value) fetchPage(isInitialLoad = false)
    }

    /**
     * toggleType
     * Adds or removes [type] from the active type filter set and rebuilds the screen state.
     *
     * @param type Type string matching a value from [allTypes].
     */
    fun toggleType(type: String) {
        val current = _selectedTypes.value
        _selectedTypes.value = if (type in current) current - type else current + type
        _screenState.value = buildState()
    }

    /**
     * setSearchText
     * Updates the live search text and re-filters the loaded tech list.
     * No API call is made — pure client-side filter.
     *
     * @param text Text to search for in tech title and tags.
     */
    fun setSearchText(text: String) {
        _searchText.value = text
        _screenState.value = buildState()
    }

    /**
     * clearAllFilters
     * Resets all active type chips and search text, restoring the full tech list.
     */
    fun clearAllFilters() {
        _selectedTypes.value = emptySet()
        _searchText.value = ""
        _screenState.value = buildState()
    }

    private fun fetchPage(isInitialLoad: Boolean) {
        viewModelScope.launch {
            _isLoadingMore.value = true
            repository.getTechs(lastId).collect { state ->
                _screenState.value = when (state) {
                    is APIState.Loading -> {
                        if (isInitialLoad) TechState.Loading else _screenState.value
                    }
                    is APIState.Error -> {
                        _isLoadingMore.value = false
                        TechState.Error(state.error, state.statusCode)
                    }
                    is APIState.Success -> {
                        _isLoadingMore.value = false
                        val page = state.data
                        if (page.size < 20) hasMoreItems = false
                        accumulatedTechs.addAll(page)
                        if (page.isNotEmpty()) lastId = page.last().id
                        _hasMore.value = hasMoreItems
                        buildState()
                    }
                }
            }
        }
    }

    private fun loadAllTypes() {
        viewModelScope.launch {
            repository.getAllTypes().collect { state ->
                if (state is APIState.Success) _allTypes.value = state.data.distinct().sorted()
            }
        }
    }

    private fun loadAllTags() {
        viewModelScope.launch {
            repository.getAllTags().collect { state ->
                if (state is APIState.Success) _allTags.value = state.data.distinct().sorted()
            }
        }
    }

    /**
     * buildState
     * Applies active type chips and search text as client-side filters over [accumulatedTechs].
     * Type filter: OR logic across selected types (case-insensitive).
     * Search filter: title or tags must contain the search text (case-insensitive).
     */
    private fun buildState(): TechState.Success {
        val types = _selectedTypes.value
        val text = _searchText.value.trim()

        val result = accumulatedTechs.filter { tech ->
            val matchesType = types.isEmpty() ||
                    types.any { t -> tech.type?.equals(t.trim(), ignoreCase = true) == true }
            val matchesText = text.isBlank() ||
                    tech.title?.contains(text, ignoreCase = true) == true ||
                    tech.tags?.contains(text, ignoreCase = true) == true
            matchesType && matchesText
        }

        return TechState.Success(techList = result, hasMore = hasMoreItems)
    }
}
